import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { UserRole } from '@/entities/session/types'
import { useSessionStore } from '@/features/session/model/sessionStore'
import { cn } from '@/shared/lib/cn'
import { useOverlay } from '@/shared/lib/overlay/useOverlay'
import { useGuardedNavigate } from '@/shared/lib/useGuardedNavigate'
import { TextInput } from '@/shared/ui/TextInput'
import { ChevronRightIcon, CloseIcon, HistoryIcon, SearchIcon, UserIcon } from '@/shared/ui/icons'
import { usePatientStore } from '@/features/patients/model/patientStore'
import { matchesPatientQuery } from '@/features/patients/model/patientSearch'
import { filterGoToPageItems, normalizeForSearch } from '@/widgets/navigation/appNavigationCatalog'
import { getGoToPageItems, type GoToPageItem } from '@/widgets/navigation/goToPageItems'
import { readRecentPaths, rememberRecentPath, resolveRecentItems } from '@/widgets/navigation/goToRecent'

const PATIENT_RESULTS_LIMIT = 5
const PATIENT_QUERY_MIN = 2
const RECENT_GROUP = 'Recentes'
const PATIENT_GROUP = 'Pacientes'

type RowKind = 'page' | 'recent' | 'patient'

interface GoToRow {
  key: string
  item: GoToPageItem
  /** Cabeçalho da seção em que a linha aparece. */
  section: string
  kind: RowKind
}

export interface GoToPagePaletteProps {
  open: boolean
  onClose: () => void
}

export const GoToPagePalette = ({ open, onClose }: GoToPagePaletteProps) => {
  const guardedNavigate = useGuardedNavigate()
  const role = useSessionStore((s) => (s.user?.role ?? 'doctor') as UserRole)
  const isSystemAdmin = useSessionStore((s) => s.user?.isSystemAdmin ?? false)
  const titleId = useId()
  const inputRef = useRef<HTMLInputElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)

  const userId = useSessionStore((s) => s.user?.id ?? null)
  const allItems = useMemo(
    () => getGoToPageItems({ role, isSystemAdmin }),
    [role, isSystemAdmin],
  )
  const patients = usePatientStore((s) => s.patients)
  const fetchPatients = usePatientStore((s) => s.fetchPatients)
  const needsPatients = open && patients.length === 0
  useEffect(() => {
    if (needsPatients) void fetchPatients().catch(() => undefined)
  }, [needsPatients, fetchPatients])

  const [recentPaths, setRecentPaths] = useState<string[]>(() => readRecentPaths(userId))

  // Sem busca: Recentes e depois todas as páginas por grupo. Com busca: pacientes primeiro
  // (é o que mais se busca), depois as páginas.
  const rows = useMemo<GoToRow[]>(() => {
    const q = query.trim()
    const list: GoToRow[] = []
    if (!normalizeForSearch(q)) {
      for (const item of resolveRecentItems(recentPaths, allItems)) {
        list.push({ key: `recent:${item.to}`, item, section: RECENT_GROUP, kind: 'recent' })
      }
    } else if (q.length >= PATIENT_QUERY_MIN) {
      patients
        .filter((patient) => matchesPatientQuery(patient, q))
        .slice(0, PATIENT_RESULTS_LIMIT)
        .forEach((patient) => {
          list.push({
            key: `patient:${patient.id}`,
            item: { to: `/patients/${patient.id}`, label: patient.name, group: 'Paciente' },
            section: PATIENT_GROUP,
            kind: 'patient',
          })
        })
    }
    for (const item of filterGoToPageItems(allItems, q)) {
      list.push({ key: `page:${item.to}:${item.label}`, item, section: item.group, kind: 'page' })
    }
    return list
  }, [allItems, patients, query, recentPaths])

  const [prevOpen, setPrevOpen] = useState(open)
  if (open !== prevOpen) {
    setPrevOpen(open)
    if (open) {
      setQuery('')
      setActiveIndex(0)
      setRecentPaths(readRecentPaths(userId))
    }
  }

  const [prevQuery, setPrevQuery] = useState(query)
  if (query !== prevQuery) {
    setPrevQuery(query)
    setActiveIndex(0)
  }

  // Voltar (web e nativo), Esc e foco (campo de busca, preso no painel): pilha única de overlays.
  const { closeThen } = useOverlay(open, {
    kind: 'dialog',
    label: 'Buscar',
    panelRef,
    initialFocusRef: inputRef,
    initialFocusDelayMs: 20,
    onRequestClose: () => onClose(),
  })

  /** Fecha a paleta e consome a entrada de histórico antes de navegar (BACK-07). */
  const goTo = useCallback(
    (row: GoToRow) => {
      if (row.kind !== 'patient') rememberRecentPath(userId, row.item.to)
      closeThen(() => {
        void guardedNavigate(row.item.to, { state: row.item.state })
      })
    },
    [closeThen, guardedNavigate, userId],
  )

  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => {
      if (rows.length === 0) return
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        setActiveIndex((i) => (i < rows.length - 1 ? i + 1 : 0))
        return
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault()
        setActiveIndex((i) => (i <= 0 ? rows.length - 1 : i - 1))
        return
      }
      if (event.key === 'Enter' && rows[activeIndex]) {
        event.preventDefault()
        goTo(rows[activeIndex])
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, rows, activeIndex, goTo])

  const listRef = useRef<HTMLUListElement>(null)
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-row-index="${activeIndex}"]`)
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' })
  }, [activeIndex])

  if (!open) return null

  return createPortal(
    <div
      className="fixed inset-0 z-[80] flex items-start justify-center bg-black/50 px-0 pt-0 max-md:flex-col md:px-4 md:pt-[12vh]"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="flex w-full max-w-lg flex-col overflow-hidden border border-[var(--color-border)] bg-[var(--color-bg-surface)] shadow-[0_16px_40px_rgba(0,0,0,0.35)] max-md:min-h-dvh max-md:max-h-dvh max-md:rounded-none max-md:border-0 md:rounded-2xl"
      >
        <h2 id={titleId} className="sr-only">
          Pesquisar páginas e pacientes
        </h2>
        <div className="border-b border-[var(--color-border)] px-3 py-2 pt-[max(0.5rem,env(safe-area-inset-top))]">
          <div className="flex items-center gap-2">
            <SearchIcon size={18} className="shrink-0 text-[var(--color-text-muted)]" />
            <TextInput
              ref={inputRef}
              bare
              autoComplete="off"
              placeholder="Pesquisar…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="min-h-11 flex-1 border-0 bg-transparent px-0 shadow-none focus:ring-0"
              aria-label="Termo de busca"
              aria-controls="synap-goto-list"
              aria-autocomplete="list"
            />
            {query ? (
              <button
                type="button"
                onClick={() => {
                  setQuery('')
                  inputRef.current?.focus()
                }}
                aria-label="Limpar busca"
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-[var(--color-text-muted)] transition-colors hover:bg-[var(--color-page)]"
              >
                <CloseIcon size={16} aria-hidden />
              </button>
            ) : (
              <kbd className="hidden rounded-md border border-[var(--color-border)] bg-[var(--color-page)] px-1.5 py-0.5 font-mono text-[10px] font-bold text-[var(--color-text-muted)] sm:inline">
                Esc
              </kbd>
            )}
          </div>
        </div>
        <ul
          ref={listRef}
          id="synap-goto-list"
          role="listbox"
          aria-label="Resultados"
          className="max-h-[min(24rem,60vh)] flex-1 overflow-y-auto p-2 max-md:max-h-none"
        >
          {rows.length === 0 ? (
            <li className="px-3 py-6 text-center text-sm text-[var(--color-text-muted)]" role="status">
              Nenhuma página ou paciente encontrado.
            </li>
          ) : (
            rows.map((row, index) => {
              const showHeader = index === 0 || rows[index - 1].section !== row.section
              const RowIcon =
                row.kind === 'recent' ? HistoryIcon : row.kind === 'patient' ? UserIcon : ChevronRightIcon
              return (
                <li key={row.key} role="presentation">
                  {showHeader ? (
                    <p
                      role="presentation"
                      className="px-3 pb-1 pt-2 text-[10px] font-bold uppercase tracking-[0.1em] text-[var(--color-text-muted)]"
                    >
                      {row.section}
                    </p>
                  ) : null}
                  <button
                    type="button"
                    role="option"
                    data-row-index={index}
                    aria-selected={index === activeIndex}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm transition-colors',
                      index === activeIndex
                        ? 'bg-[var(--color-mint-soft)] text-[var(--color-text-primary)]'
                        : 'text-[var(--color-text-primary)] hover:bg-[var(--color-page)]',
                    )}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => goTo(row)}
                  >
                    <RowIcon size={16} aria-hidden className="shrink-0 text-[var(--color-text-muted)]" />
                    <span className="min-w-0 flex-1 truncate font-semibold">{row.item.label}</span>
                    {row.kind === 'recent' ? (
                      <span className="shrink-0 text-[11px] font-medium text-[var(--color-text-muted)]">
                        {row.item.group}
                      </span>
                    ) : null}
                  </button>
                </li>
              )
            })
          )}
        </ul>
        <p className="border-t border-[var(--color-border)] px-4 py-2 text-[11px] text-[var(--color-text-muted)]">
          ↑↓ navegar · Enter abrir · Esc fechar
        </p>
      </div>
    </div>,
    document.body,
  )
}
